import { useEffect, useState } from "react";
import { collection, getDocs, query, orderBy } from "firebase/firestore";
import { db } from "../firebase";
import { X, MapPin, AlertTriangle, HeartHandshake, ArrowRight, Copy, Phone, Mail, IndianRupee, Landmark } from "lucide-react";
import toast, { Toaster } from "react-hot-toast";

const ORG_PHONE = "+91 7669990505";
const ORG_EMAIL = "htstrust@gmail.com";

export default function CampaignPopup() {
  const [campaign, setCampaign] = useState(null);
  const [visible,  setVisible]  = useState(false);
  const [closing,  setClosing]  = useState(false);
  const [donating, setDonating] = useState(false);

  useEffect(() => {
    const dismissed = sessionStorage.getItem("htst_campaign_popup_dismissed");
    if (dismissed) return;

    async function load() {
      try {
        const snap = await getDocs(query(collection(db, "campaigns"), orderBy("createdAt", "desc")));
        const all = snap.docs.map(d => ({ id: d.id, ...d.data() })).filter(c => c.active !== false);
        // Only pop up for an urgent campaign — avoids popup fatigue alongside EventPopup
        const urgent = all.find(c => c.urgent);
        if (urgent) {
          setCampaign(urgent);
          setTimeout(() => setVisible(true), 100); // near-immediate
        }
      } catch (e) {
        console.error("CampaignPopup load error:", e);
      }
    }
    load();
  }, []);

  function handleClose() {
    setClosing(true);
    setTimeout(() => {
      setVisible(false);
      setClosing(false);
      sessionStorage.setItem("htst_campaign_popup_dismissed", "true");
    }, 300);
  }

  function pct(c) {
    if (!c?.goalAmount) return 0;
    return Math.min(100, Math.round(((c.raisedAmount || 0) / c.goalAmount) * 100));
  }

  function copy(value, label) {
    navigator.clipboard.writeText(value);
    toast.success(`${label} copied!`);
  }

  if (!campaign || !visible) return null;

  return (
    <>
      <Toaster position="top-center" />
      <style>{`
        @keyframes campPopIn {
          from { opacity:0; transform:scale(0.92) translateY(20px); }
          to   { opacity:1; transform:scale(1)    translateY(0); }
        }
        @keyframes campPopOut {
          from { opacity:1; transform:scale(1)    translateY(0); }
          to   { opacity:0; transform:scale(0.92) translateY(20px); }
        }
        @keyframes campOverlayIn  { from{opacity:0} to{opacity:1} }
        @keyframes campOverlayOut { from{opacity:1} to{opacity:0} }
        .camp-popup-overlay {
          position:fixed; inset:0; z-index:10001;
          background:rgba(0,0,0,0.85);
          display:flex; align-items:center; justify-content:center;
          padding:20px;
          animation: ${closing ? "campOverlayOut" : "campOverlayIn"} 0.3s ease forwards;
          backdrop-filter:blur(6px);
        }
        .camp-popup-card {
          background:#111009;
          border:1px solid rgba(239,68,68,0.25);
          border-radius:20px;
          overflow:hidden;
          width:100%;
          max-width:480px;
          position:relative;
          animation: ${closing ? "campPopOut" : "campPopIn"} 0.35s cubic-bezier(0.34,1.56,0.64,1) forwards;
          box-shadow:0 32px 80px rgba(0,0,0,0.6);
          max-height:88vh;
          overflow-y:auto;
        }
        .camp-popup-close {
          position:absolute; top:12px; right:12px; z-index:2;
          width:34px; height:34px; border-radius:50%;
          background:rgba(0,0,0,0.6); border:1px solid rgba(255,255,255,0.15);
          display:flex; align-items:center; justify-content:center;
          cursor:pointer; color:#fff; transition:background 0.2s;
        }
        .camp-popup-close:hover { background:rgba(239,68,68,0.4); }
        .camp-popup-img { width:100%; max-height:220px; object-fit:cover; display:block; }
        .camp-popup-body { padding:20px 22px 24px; }
        .camp-popup-urgent-badge {
          display:inline-flex; align-items:center; gap:5px; font-size:10px; font-weight:800;
          padding:4px 11px; border-radius:50px; color:#fff; background:#ef4444;
          text-transform:uppercase; letter-spacing:0.6px; margin-bottom:10px;
        }
        .camp-popup-title {
          font-family:'Playfair Display',serif;
          font-size:19px; font-weight:800; color:#fef3e2;
          margin:0 0 6px; line-height:1.3;
        }
        .camp-popup-loc { display:flex; align-items:center; gap:5px; font-size:11px; color:#78716c; margin-bottom:12px; }
        .camp-popup-summary { font-size:13px; color:#a8a29e; line-height:1.6; margin:0 0 16px; }
        .camp-popup-track { height:9px; background:rgba(255,255,255,0.07); border-radius:50px; overflow:hidden; margin-bottom:8px; }
        .camp-popup-fill { height:100%; border-radius:50px; background:linear-gradient(90deg,#f59e0b,#fbbf24); transition:width 0.8s ease; }
        .camp-popup-nums { display:flex; justify-content:space-between; font-size:11.5px; color:#78716c; margin-bottom:18px; }
        .camp-popup-raised { font-weight:800; color:#fef3e2; }
        .camp-popup-footer {
          display:flex; align-items:center; justify-content:space-between; gap:10px;
          padding-top:14px; border-top:1px solid rgba(255,255,255,0.06);
        }
        .camp-popup-dismiss {
          font-size:12px; color:#52504d; cursor:pointer;
          background:none; border:none; font-family:inherit; white-space:nowrap;
        }
        .camp-popup-dismiss:hover { color:#78716c; }
        .camp-popup-cta {
          display:inline-flex; align-items:center; gap:6px;
          background:#f59e0b; color:#0a0908;
          border:none; border-radius:50px;
          padding:9px 18px; font-size:13px; font-weight:800;
          cursor:pointer; font-family:inherit; transition:all 0.2s;
          white-space:nowrap;
        }
        .camp-popup-cta:hover { transform:translateY(-1px); box-shadow:0 6px 20px rgba(245,158,11,0.4); }

        .camp-donate-panel { padding:0 22px 22px; }
        .camp-donate-row {
          display:flex; align-items:center; gap:10px; padding:11px 12px;
          background:rgba(255,255,255,0.03); border:1px solid rgba(255,255,255,0.06);
          border-radius:10px; margin-bottom:8px;
        }
        .camp-donate-icon { width:30px; height:30px; border-radius:8px; background:rgba(245,158,11,0.12); display:flex; align-items:center; justify-content:center; flex-shrink:0; }
        .camp-donate-label { font-size:9.5px; font-weight:700; color:#52504d; text-transform:uppercase; letter-spacing:0.5px; }
        .camp-donate-val { font-size:12.5px; font-weight:600; color:#fef3e2; }
        .camp-donate-copy { width:28px; height:28px; border-radius:7px; background:rgba(255,255,255,0.06); border:1px solid rgba(255,255,255,0.1); color:#a8a29e; cursor:pointer; display:flex; align-items:center; justify-content:center; flex-shrink:0; }
        .camp-donate-divider { font-size:9.5px; font-weight:800; color:#52504d; text-transform:uppercase; letter-spacing:0.8px; margin:12px 0 6px; }

        @media(max-width:480px) {
          .camp-popup-card { border-radius:16px; }
          .camp-popup-img { max-height:170px; }
          .camp-popup-title { font-size:16px; }
          .camp-popup-footer { flex-wrap:wrap; }
        }
      `}</style>

      <div className="camp-popup-overlay" onClick={handleClose}>
        <div className="camp-popup-card" onClick={e => e.stopPropagation()}>
          <button className="camp-popup-close" onClick={handleClose}><X size={16} /></button>

          {campaign.imageUrl && (
            <img src={campaign.imageUrl} alt={campaign.title} className="camp-popup-img"
              onError={e => { e.target.style.display = "none"; }} />
          )}

          <div className="camp-popup-body">
            <div className="camp-popup-urgent-badge"><AlertTriangle size={11} /> Urgent Appeal</div>
            <h2 className="camp-popup-title">{campaign.title}</h2>
            {campaign.location && (
              <div className="camp-popup-loc"><MapPin size={11} color="#f59e0b" /> {campaign.location}</div>
            )}
            {campaign.summary && <p className="camp-popup-summary">{campaign.summary}</p>}

            <div className="camp-popup-track">
              <div className="camp-popup-fill" style={{ width: `${pct(campaign)}%` }} />
            </div>
            <div className="camp-popup-nums">
              <span><span className="camp-popup-raised">₹{(campaign.raisedAmount || 0).toLocaleString()}</span> raised</span>
              <span>{pct(campaign)}% of ₹{(campaign.goalAmount || 0).toLocaleString()} goal</span>
            </div>

            {!donating ? (
              <div className="camp-popup-footer">
                <button className="camp-popup-dismiss" onClick={handleClose}>Not now</button>
                <button className="camp-popup-cta" onClick={() => setDonating(true)}>
                  Donate Now <ArrowRight size={13} />
                </button>
              </div>
            ) : null}
          </div>

          {donating && (
            <div className="camp-donate-panel">
              {campaign.upiId && (
                <div className="camp-donate-row">
                  <div className="camp-donate-icon"><IndianRupee size={14} color="#f59e0b" /></div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div className="camp-donate-label">UPI ID</div>
                    <div className="camp-donate-val">{campaign.upiId}</div>
                  </div>
                  <button className="camp-donate-copy" onClick={() => copy(campaign.upiId, "UPI ID")}><Copy size={12} /></button>
                </div>
              )}

              {campaign.accountNumber && (
                <>
                  <div className="camp-donate-divider">Bank Transfer</div>
                  {campaign.accountName && (
                    <div className="camp-donate-row">
                      <div className="camp-donate-icon"><Landmark size={14} color="#f59e0b" /></div>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div className="camp-donate-label">Account Name</div>
                        <div className="camp-donate-val">{campaign.accountName}</div>
                      </div>
                      <button className="camp-donate-copy" onClick={() => copy(campaign.accountName, "Account Name")}><Copy size={12} /></button>
                    </div>
                  )}
                  <div className="camp-donate-row">
                    <div className="camp-donate-icon"><Landmark size={14} color="#f59e0b" /></div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div className="camp-donate-label">Account Number</div>
                      <div className="camp-donate-val">{campaign.accountNumber}</div>
                    </div>
                    <button className="camp-donate-copy" onClick={() => copy(campaign.accountNumber, "Account Number")}><Copy size={12} /></button>
                  </div>
                  {campaign.ifscCode && (
                    <div className="camp-donate-row">
                      <div className="camp-donate-icon"><Landmark size={14} color="#f59e0b" /></div>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div className="camp-donate-label">IFSC Code</div>
                        <div className="camp-donate-val">{campaign.ifscCode}</div>
                      </div>
                      <button className="camp-donate-copy" onClick={() => copy(campaign.ifscCode, "IFSC Code")}><Copy size={12} /></button>
                    </div>
                  )}
                  {campaign.bankName && (
                    <div className="camp-donate-row">
                      <div className="camp-donate-icon"><Landmark size={14} color="#f59e0b" /></div>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div className="camp-donate-label">Bank</div>
                        <div className="camp-donate-val">{campaign.bankName}</div>
                      </div>
                      <button className="camp-donate-copy" onClick={() => copy(campaign.bankName, "Bank")}><Copy size={12} /></button>
                    </div>
                  )}
                </>
              )}

              <div className="camp-donate-divider">Contact</div>
              <div className="camp-donate-row">
                <div className="camp-donate-icon"><Phone size={14} color="#f59e0b" /></div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div className="camp-donate-label">Call / WhatsApp</div>
                  <div className="camp-donate-val">{campaign.contactPhone || ORG_PHONE}</div>
                </div>
                <button className="camp-donate-copy" onClick={() => copy(campaign.contactPhone || ORG_PHONE, "Number")}><Copy size={12} /></button>
              </div>
              <div className="camp-donate-row">
                <div className="camp-donate-icon"><Mail size={14} color="#f59e0b" /></div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div className="camp-donate-label">Email</div>
                  <div className="camp-donate-val">{ORG_EMAIL}</div>
                </div>
                <button className="camp-donate-copy" onClick={() => copy(ORG_EMAIL, "Email")}><Copy size={12} /></button>
              </div>
              <button className="camp-popup-dismiss" style={{ marginTop: 6 }} onClick={handleClose}>Close</button>
            </div>
          )}
        </div>
      </div>
    </>
  );
}